class TwoPmStickyCta extends HTMLElement {
  connectedCallback() {
    this.showAfter = Number(this.dataset.showAfter) || 60;
    this.hideBeforeBottom = Number(this.dataset.hideBeforeBottom) || 50;
    this.defaultProductId = this.dataset.defaultProductId;
    this.defaultVariantId = this.dataset.defaultVariantId;
    this.products = this.readProducts();
    this.currentProduct = this.products.find((product) => String(product.id) === this.defaultProductId) || this.products[0];
    this.currentVariant = null;
    this.isTicking = false;
    this.handleScroll = this.handleScroll.bind(this);
    this.updateVisibility = this.updateVisibility.bind(this);
    this.handleDocumentClick = this.handleDocumentClick.bind(this);
    this.handleKeydown = this.handleKeydown.bind(this);

    this.productLabel = this.querySelector('[data-product-label]');
    this.variantLabel = this.querySelector('[data-variant-label]');
    this.prices = this.querySelectorAll('[data-price]');
    this.image = this.querySelector('.two-pm-sticky-cta__image');
    this.imageWrap = this.querySelector('.two-pm-sticky-cta__image-wrap');
    this.variantList = this.querySelector('[data-dropdown="variant"] [data-dropdown-list]');
    this.addButton = this.querySelector('[data-add-to-cart]');

    this.addEventListener('click', (event) => this.handleClick(event));
    this.addEventListener('keydown', this.handleKeydown);
    document.addEventListener('click', this.handleDocumentClick);
    window.addEventListener('scroll', this.handleScroll, { passive: true });
    window.addEventListener('resize', this.updateVisibility, { passive: true });

    if (this.currentProduct) this.selectProduct(this.currentProduct.id);
    this.updateVisibility();
  }

  disconnectedCallback() {
    document.removeEventListener('click', this.handleDocumentClick);
    window.removeEventListener('scroll', this.handleScroll);
    window.removeEventListener('resize', this.updateVisibility);
  }

  readProducts() {
    const data = this.querySelector('[data-product-data]');
    if (!data) return [];

    try {
      return JSON.parse(data.textContent);
    } catch (error) {
      console.error('Unable to read 2pm sticky CTA products.', error);
      return [];
    }
  }

  handleScroll() {
    if (this.isTicking) return;

    this.isTicking = true;
    window.requestAnimationFrame(() => {
      this.updateVisibility();
      this.isTicking = false;
    });
  }

  updateVisibility() {
    const scrollTop = window.scrollY || document.documentElement.scrollTop;
    const distanceToBottom = document.documentElement.scrollHeight - (scrollTop + window.innerHeight);
    const shouldShow = scrollTop >= this.showAfter && distanceToBottom > this.hideBeforeBottom;

    this.classList.toggle('is-visible', shouldShow);
    this.setAttribute('aria-hidden', String(!shouldShow));
    this.toggleAttribute('inert', !shouldShow);
  }

  handleClick(event) {
    const trigger = event.target.closest('[data-dropdown-trigger]');
    if (trigger) {
      this.toggleDropdown(trigger.closest('[data-dropdown]'));
      return;
    }

    const productOption = event.target.closest('[data-product-id]');
    if (productOption) {
      this.selectProduct(productOption.dataset.productId);
      this.closeDropdowns();
      return;
    }

    const variantOption = event.target.closest('[data-variant-id]');
    if (variantOption) {
      this.selectVariant(variantOption.dataset.variantId);
      this.closeDropdowns();
      return;
    }

    if (event.target.closest('[data-add-to-cart]')) this.addToCart();
  }

  handleDocumentClick(event) {
    if (!this.contains(event.target)) this.closeDropdowns();
  }

  handleKeydown(event) {
    if (event.key === 'Escape') {
      const dropdown = event.target.closest('[data-dropdown]');
      this.closeDropdowns();
      dropdown?.querySelector('[data-dropdown-trigger]')?.focus();
    }
  }

  toggleDropdown(dropdown) {
    const isOpen = dropdown.querySelector('[data-dropdown-trigger]').getAttribute('aria-expanded') === 'true';
    this.closeDropdowns();
    if (!isOpen) this.setDropdownState(dropdown, true);
  }

  closeDropdowns() {
    this.querySelectorAll('[data-dropdown]').forEach((dropdown) => this.setDropdownState(dropdown, false));
  }

  setDropdownState(dropdown, isOpen) {
    const trigger = dropdown.querySelector('[data-dropdown-trigger]');
    const list = dropdown.querySelector('[data-dropdown-list]');
    trigger.setAttribute('aria-expanded', String(isOpen));
    list.hidden = !isOpen;
  }

  selectProduct(productId) {
    const product = this.products.find((item) => String(item.id) === String(productId));
    if (!product) return;

    this.currentProduct = product;
    this.productLabel.textContent = product.title;
    this.querySelectorAll('[data-product-id]').forEach((option) => {
      option.setAttribute('aria-selected', String(String(option.dataset.productId) === String(product.id)));
    });

    if (product.image) {
      if (!this.image) {
        this.image = document.createElement('img');
        this.image.className = 'two-pm-sticky-cta__image';
        this.imageWrap.append(this.image);
      }
      this.image.src = product.image;
      this.image.alt = product.imageAlt || product.title;
    }

    this.renderVariantOptions();
    const defaultVariant = String(product.id) === this.defaultProductId
      ? product.variants.find((variant) => String(variant.id) === this.defaultVariantId)
      : null;
    const firstAvailableVariant = product.variants.find((variant) => variant.available) || product.variants[0];
    this.selectVariant(defaultVariant?.id || firstAvailableVariant?.id);
  }

  renderVariantOptions() {
    this.variantList.replaceChildren();
    this.currentProduct.variants.forEach((variant) => {
      const item = document.createElement('li');
      const button = document.createElement('button');
      button.className = 'two-pm-sticky-cta__dropdown-option';
      button.type = 'button';
      button.setAttribute('role', 'option');
      button.dataset.variantId = variant.id;
      button.setAttribute('aria-selected', 'false');
      button.disabled = !variant.available;
      button.textContent = variant.title;
      item.append(button);
      this.variantList.append(item);
    });
  }

  selectVariant(variantId) {
    const variant = this.currentProduct?.variants.find((item) => String(item.id) === String(variantId));
    if (!variant) {
      this.currentVariant = null;
      this.variantLabel.textContent = 'Select option';
      this.prices.forEach((price) => {
        price.textContent = '';
      });
      this.addButton.disabled = true;
      return;
    }

    this.currentVariant = variant;
    this.variantLabel.textContent = variant.title;
    this.prices.forEach((price) => {
      price.textContent = variant.priceFormatted;
    });
    this.addButton.disabled = !variant.available;
    this.variantList.querySelectorAll('[data-variant-id]').forEach((option) => {
      option.setAttribute('aria-selected', String(String(option.dataset.variantId) === String(variant.id)));
    });
  }

  async addToCart() {
    if (!this.currentVariant || !this.currentVariant.available || this.addButton.disabled) return;

    const item = { id: this.currentVariant.id, quantity: 1 };
    if (this.currentVariant.sellingPlanId) item.selling_plan = this.currentVariant.sellingPlanId;

    this.addButton.disabled = true;
    this.addButton.setAttribute('aria-busy', 'true');

    try {
      const cartApi = window.NSHybridCart?.cartAPI;
      if (cartApi?.addToCart) {
        const result = await cartApi.addToCart(item);
        if (!result) throw new Error('Unable to add product to cart.');
      } else {
        const response = await fetch(`${window.Shopify?.routes?.root || '/'}cart/add.js`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
          body: JSON.stringify({ items: [item] })
        });
        if (!response.ok) throw new Error('Unable to add product to cart.');
      }

      await window.NSHybridCart?.open?.();
    } catch (error) {
      console.error('2pm sticky CTA add to cart failed.', error);
    } finally {
      this.addButton.disabled = !this.currentVariant.available;
      this.addButton.removeAttribute('aria-busy');
    }
  }
}

if (!customElements.get('two-pm-sticky-cta')) {
  customElements.define('two-pm-sticky-cta', TwoPmStickyCta);
}
